import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Hash, Save, RefreshCw, Users, Package } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

const KIND_META = {
  member: {
    icon: Users,
    title: "Üye Numaralandırma",
    desc: "Yeni eklenen üyelere otomatik atanan numara biçimi.",
    placeholder: "UYE-",
  },
  package: {
    icon: Package,
    title: "Paket Numaralandırma",
    desc: "Üyeye satılan paketlere atanan numara biçimi.",
    placeholder: "PKT-",
  },
};

const PADDING_OPTIONS = [3, 4, 5, 6];

export default function NumberingSettings({ businessId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({ member: false, package: false });
  const [backfilling, setBackfilling] = useState(false);
  const [data, setData] = useState({
    member_numbering: { prefix: "M", padding: 4, next_number: 1 },
    package_numbering: { prefix: "P", padding: 4, next_number: 1 },
    preview: { member: "M0001", package: "P0001" },
  });
  // Local edited state per kind so previews update live without saving
  const [draft, setDraft] = useState({
    member: { prefix: "M", padding: 4, next_number: 1 },
    package: { prefix: "P", padding: 4, next_number: 1 },
  });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/businesses/${businessId}/numbering-settings`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Yüklenemedi");
      const d = await res.json();
      setData(d);
      setDraft({
        member: { ...d.member_numbering },
        package: { ...d.package_numbering },
      });
    } catch (e) {
      toast.error("Numaralandırma ayarları yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const formatPreview = (cfg) => {
    const seq = String(cfg.next_number).padStart(cfg.padding, "0");
    return `${cfg.prefix}${seq}`;
  };

  const handleSave = async (kind) => {
    setSaving((s) => ({ ...s, [kind]: true }));
    try {
      const res = await fetch(
        `${API}/businesses/${businessId}/numbering-settings/${kind}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            prefix: draft[kind].prefix,
            padding: Number(draft[kind].padding),
            next_number: Number(draft[kind].next_number),
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Kaydedilemedi");
      }
      const d = await res.json();
      setData(d);
      setDraft({
        member: { ...d.member_numbering },
        package: { ...d.package_numbering },
      });
      toast.success("Ayarlar kaydedildi");
    } catch (e) {
      toast.error(e.message || "Kaydedilemedi");
    } finally {
      setSaving((s) => ({ ...s, [kind]: false }));
    }
  };

  const handleBackfill = async () => {
    if (!window.confirm("Numarası olmayan tüm satılmış paketlere kronolojik sırayla numara atanacak. Devam edilsin mi?")) return;
    setBackfilling(true);
    try {
      const res = await fetch(
        `${API}/businesses/${businessId}/packages/backfill-numbers`,
        { method: "POST", credentials: "include" },
      );
      if (!res.ok) throw new Error("Numaralandırma başarısız");
      const d = await res.json();
      toast.success(d.message || "Tamamlandı");
      await fetchSettings();
    } catch (e) {
      toast.error(e.message || "Hata");
    } finally {
      setBackfilling(false);
    }
  };

  const renderCard = (kind) => {
    const meta = KIND_META[kind];
    const Icon = meta.icon;
    const cfg = draft[kind];
    const saved = data[kind === "member" ? "member_numbering" : "package_numbering"];
    const isDirty =
      cfg.prefix !== saved.prefix ||
      Number(cfg.padding) !== Number(saved.padding) ||
      Number(cfg.next_number) !== Number(saved.next_number);

    return (
      <Card className="border-0 shadow-sm" data-testid={`numbering-card-${kind}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Icon className="w-4 h-4" />
            {meta.title}
          </CardTitle>
          <p className="text-xs text-grey-500">{meta.desc}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Önek</Label>
              <Input
                value={cfg.prefix}
                placeholder={meta.placeholder}
                maxLength={20}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    [kind]: { ...d[kind], prefix: e.target.value },
                  }))
                }
                className="rounded-xl"
                data-testid={`numbering-prefix-${kind}`}
              />
            </div>
            <div>
              <Label className="text-xs">Hane sayısı</Label>
              <Select
                value={String(cfg.padding)}
                onValueChange={(v) =>
                  setDraft((d) => ({
                    ...d,
                    [kind]: { ...d[kind], padding: Number(v) },
                  }))
                }
              >
                <SelectTrigger className="rounded-xl" data-testid={`numbering-padding-${kind}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PADDING_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} hane (örn. {String(1).padStart(n, "0")})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sonraki numara</Label>
              <Input
                type="number"
                min="1"
                value={cfg.next_number}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    [kind]: { ...d[kind], next_number: Number(e.target.value) || 1 },
                  }))
                }
                className="rounded-xl"
                data-testid={`numbering-next-${kind}`}
              />
              <p className="text-[11px] text-grey-500 mt-1">
                Mevcut maks. + 1'den küçükse otomatik artar.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-grey-500 font-semibold">
                Önizleme
              </p>
              <p
                className="font-heading font-bold text-xl mt-1"
                style={{ color: "#1B365D" }}
                data-testid={`numbering-preview-${kind}`}
              >
                {formatPreview(cfg)}
              </p>
            </div>
            <Button
              onClick={() => handleSave(kind)}
              disabled={!isDirty || saving[kind]}
              className="rounded-xl"
              style={{ background: "#DA291C", color: "white" }}
              data-testid={`numbering-save-${kind}`}
            >
              <Save className="w-4 h-4 mr-1" />
              {saving[kind] ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </div>

          {kind === "package" && (
            <div className="pt-3 border-t border-grey-100 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Eski paketleri yeniden numaralandır</p>
                <p className="text-xs text-grey-500">
                  Numarası olmayan satılmış paketlere kronolojik sıraya göre numara atar.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleBackfill}
                disabled={backfilling}
                className="rounded-xl"
                data-testid="numbering-backfill-package"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${backfilling ? "animate-spin" : ""}`} />
                {backfilling ? "Çalışıyor…" : "Yeniden Numaralandır"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-8 text-center text-grey-500 text-sm">
          Yükleniyor…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm" data-testid="numbering-settings-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <Hash className="w-4 h-4" />
          Numaralandırma
        </CardTitle>
        <p className="text-xs text-grey-500">
          Üyelere ve satılan paketlere atanan numaraların biçimini özelleştirin.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {renderCard("member")}
        {renderCard("package")}
      </CardContent>
    </Card>
  );
}
